<template>
  <div>
    <v-container class="pa-4 pa-sm-8" fluid>
      <v-row class="mt-3 mt-sm-0">
        <h2 class="dark--text text-h5 font-weight-bold ms-3">System Log</h2>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12" md="3">
          <v-text-field
            v-model="filters.loginName"
            label="Login name"
            dense
            outlined
            clearable
            @keyup.enter="applyFilters"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.isLoggedOut"
            :items="statusOptions"
            label="Status"
            dense
            outlined
            clearable
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-btn color="primary" class="mt-1" @click="applyFilters">Filter</v-btn>
        </v-col>
      </v-row>

      <v-data-table
        :mobile-breakpoint="0"
        :headers="headers"
        :items="itemsWithSno"
        class="elevation-1 mt-4"
        :loading="loading"
        :server-items-length="totalItemsCount"
        :options.sync="tableOptions"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      >
        <template v-slot:item.loginAt="{ item }">
          {{ formatDate(item.loginAt) }}
        </template>
        <template v-slot:item.logoutAt="{ item }">
          {{ item.logoutAt ? formatDate(item.logoutAt) : "-" }}
        </template>
        <template v-slot:item.isLoggedOut="{ item }">
          <v-chip small :color="item.isLoggedOut ? 'grey' : 'success'" dark>
            {{ item.isLoggedOut ? "Logged out" : "Active" }}
          </v-chip>
        </template>
        <template v-slot:item.locationName="{ item }">
          <div v-if="hasLocation(item)" class="tw-text-sm">
            <div v-if="item.locationName" class="font-weight-medium">{{ item.locationName }}</div>
            <div v-if="item.street" class="grey--text">{{ item.street }}</div>
            <div v-if="formatAddressLine(item)" class="grey--text">{{ formatAddressLine(item) }}</div>
            <div v-if="item.latitude != null && item.longitude != null" class="grey--text text-caption">
              {{ item.latitude }}, {{ item.longitude }}
            </div>
            <a
              v-if="googleMapsUrl(item.latitude, item.longitude)"
              :href="googleMapsUrl(item.latitude, item.longitude)"
              target="_blank"
              rel="noopener noreferrer"
              class="primary--text text-caption"
            >
              View on Google Maps
            </a>
          </div>
          <span v-else>-</span>
        </template>
        <template v-slot:item.ipLocationName="{ item }">
          <div v-if="hasIpLocation(item)" class="tw-text-sm">
            <div v-if="item.ipLocationName" class="font-weight-medium">{{ item.ipLocationName }}</div>
            <div v-if="item.ipStreet" class="grey--text">{{ item.ipStreet }}</div>
            <div v-if="formatIpAddressLine(item)" class="grey--text">{{ formatIpAddressLine(item) }}</div>
            <div v-if="item.ipLatitude != null && item.ipLongitude != null" class="grey--text text-caption">
              {{ item.ipLatitude }}, {{ item.ipLongitude }}
            </div>
            <a
              v-if="googleMapsUrl(item.ipLatitude, item.ipLongitude)"
              :href="googleMapsUrl(item.ipLatitude, item.ipLongitude)"
              target="_blank"
              rel="noopener noreferrer"
              class="primary--text text-caption"
            >
              View on Google Maps
            </a>
          </div>
          <span v-else>-</span>
        </template>
        <template v-slot:item.clientInfo="{ item }">
          <div v-if="hasClientInfo(item)" class="tw-text-sm">
            <div v-if="item.ipAddress" class="grey--text">
              <span class="font-weight-medium dark--text">IP:</span>
              {{ item.ipAddress }}<span v-if="item.ipVersion"> ({{ item.ipVersion }})</span>
            </div>
            <div v-if="item.browser" class="grey--text">
              <span class="font-weight-medium dark--text">Browser:</span>
              {{ item.browser }}<span v-if="item.browserVersion"> {{ item.browserVersion }}</span>
            </div>
            <div v-if="item.os || item.deviceType" class="grey--text">
              <span class="font-weight-medium dark--text">OS:</span>
              {{ [item.os, item.deviceType].filter(Boolean).join(" · ") }}
            </div>
            <div v-if="item.timezone" class="grey--text">
              <span class="font-weight-medium dark--text">Timezone:</span>
              {{ item.timezone }}
            </div>
            <div v-if="item.screenResolution" class="grey--text">
              <span class="font-weight-medium dark--text">Screen:</span>
              {{ item.screenResolution }}
            </div>
          </div>
          <span v-else>-</span>
        </template>
        <template v-slot:item.duration="{ item }">
          {{ formatDuration(item.duration) }}
        </template>
        <template v-slot:item.logoutType="{ item }">
          {{ item.logoutType || "-" }}
        </template>
      </v-data-table>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";

export default {
  created() {
    if (!this.hasRole(this.AdminUserPermissions.SYSTEM_LOG_REPORT_VIEW)) {
      this.$router.replace({ path: "/dashboard" });
    }
  },
  data() {
    return {
      loading: false,
      totalItemsCount: 0,
      sessions: [],
      tableOptions: { page: 1, itemsPerPage: 25 },
      filters: {
        loginName: "",
        isLoggedOut: null,
      },
      statusOptions: [
        { text: "Active", value: false },
        { text: "Logged out", value: true },
      ],
      headers: [
        { text: "#", value: "slNo", sortable: false, width: 70 },
        { text: "Login Name", value: "loginName", width: 180 },
        { text: "User", value: "userName", width: 180 },
        { text: "Login Date & Time", value: "loginAt", width: 180 },
        { text: "Status", value: "isLoggedOut", width: 120 },
        { text: "Logout Date & Time", value: "logoutAt", width: 180 },
        { text: "Duration", value: "duration", width: 120 },
        { text: "Logout Type", value: "logoutType", width: 150 },
        { text: "Location", value: "locationName", width: 320 },
        { text: "IP Location", value: "ipLocationName", width: 320 },
        { text: "Client / Device", value: "clientInfo", width: 260 },
      ],
    };
  },
  apollo: {
    getAdminLoginSessions() {
      return {
        query: require("../api/getAdminLoginSessions.graphql"),
        variables() {
          const { page, itemsPerPage } = this.tableOptions;
          return {
            filter: this.buildFilter(),
            input: {
              skip: (page - 1) * itemsPerPage,
              limit: itemsPerPage,
            },
          };
        },
        result({ data, loading }) {
          this.loading = loading;
          if (data && data.getAdminLoginSessions) {
            this.sessions = data.getAdminLoginSessions.sessions || [];
            this.totalItemsCount = data.getAdminLoginSessions.totalCount || 0;
          }
        },
      };
    },
  },
  watch: {
    tableOptions: {
      deep: true,
      handler() {
        this.$apollo.queries.getAdminLoginSessions.refetch();
      },
    },
  },
  computed: {
    itemsWithSno() {
      const { page, itemsPerPage } = this.tableOptions;
      const offset = (page - 1) * itemsPerPage;
      return this.sessions.map((session, index) => ({
        ...session,
        slNo: offset + index + 1,
        userName: session.user
          ? [session.user.firstnameEn, session.user.lastnameEn].filter(Boolean).join(" ") ||
            session.user.username ||
            session.user.phone
          : session.loginName,
      }));
    },
  },
  methods: {
    buildFilter() {
      const filter = {};
      if (this.filters.loginName) filter.loginName = this.filters.loginName;
      if (this.filters.isLoggedOut != null) filter.isLoggedOut = this.filters.isLoggedOut;
      return filter;
    },
    applyFilters() {
      this.tableOptions.page = 1;
      this.$apollo.queries.getAdminLoginSessions.refetch();
    },
    formatDate(value) {
      return value ? moment(value).format("DD MMM YYYY, hh:mm A") : "-";
    },
    formatDuration(seconds) {
      if (seconds == null) return "-";
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
      if (mins > 0) return `${mins}m ${secs}s`;
      return `${secs}s`;
    },
    hasLocation(item) {
      return (
        item.locationName ||
        item.street ||
        item.city ||
        item.area ||
        (item.latitude != null && item.longitude != null)
      );
    },
    formatAddressLine(item) {
      return [item.area, item.city, item.state, item.postalCode, item.country]
        .filter(Boolean)
        .join(", ");
    },
    hasIpLocation(item) {
      return (
        item.ipLocationName ||
        item.ipStreet ||
        item.ipCity ||
        item.ipArea ||
        (item.ipLatitude != null && item.ipLongitude != null)
      );
    },
    formatIpAddressLine(item) {
      return [item.ipArea, item.ipCity, item.ipState, item.ipPostalCode, item.ipCountry]
        .filter(Boolean)
        .join(", ");
    },
    googleMapsUrl(latitude, longitude) {
      if (latitude == null || longitude == null) return null;
      return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    },
    hasClientInfo(item) {
      return (
        item.ipAddress ||
        item.browser ||
        item.os ||
        item.deviceType ||
        item.timezone ||
        item.screenResolution
      );
    },
  },
};
</script>
